/*
 * NCII — Free shipping unlocked by product (v3)
 * ---------------------------------------------
 * Some products (e.g. Breath Easy Inhaler) grant free shipping regardless of
 * cart value, via a Puco automatic shipping discount. This makes the cart
 * drawer's checkpoints bar reflect that.
 *
 * WHY THIS FILE EXISTS (instead of editing secondary.js):
 * Shrine ships assets/secondary.js as MINIFIED build output. A theme update
 * overwrites it completely, so a hand-edit there dies on every upgrade. This
 * file overrides CartCheckpointsBar.prototype.update() after the class
 * registers. Shrine doesn't know this file exists, so updates leave it alone.
 *
 * AFTER A SHRINE UPDATE, re-add ONE line to layout/theme.liquid:
 *   <script src="{{ 'ncii-cart-unlock.js' | asset_url }}" defer></script>
 *
 * v3 CHANGE — why the previous version got stuck:
 * v2 wrote a cached cart total back into data-cart-total-cents and re-ran
 * Shrine's update() to recompute. When that cache was stale, it poisoned the
 * attribute, so the "reset to baseline" path read the bad number and the bar
 * stayed on the success message after the unlock product was removed.
 * v3 never mutates the DOM attribute and never relies on cached state: every
 * update() fetches /cart.js and renders BOTH the locked and unlocked states
 * from that single fresh result.
 *
 * v4: any subscription line also unlocks free shipping (matches the
 * "Free Shipping – Any Subscription in Cart" automatic discount).
 *
 * TO ADD ANOTHER UNLOCKING PRODUCT: add its handle to UNLOCK_HANDLES, and add
 * a matching rule in Puco so checkout agrees with what the bar claims.
 *
 * ASSUMPTION: single-currency store (USD). Multi-currency would need the
 * presentment rate applied to the money formatting below.
 */
(function () {
  'use strict';

  // NOTE: the live handle contains a typo ("shippiing"). Copied exactly as-is —
  // do not "correct" it unless the product's URL handle changes in Shopify.
  var UNLOCK_HANDLES = ['breath-easy-inhaler-free-shippiing'];

  var ELEMENT = 'cart-checkpoints-bar';

  // Brief in-flight cache so several bars in one cart operation share a fetch.
  var pending = null;
  var pendingAt = 0;

  function readCart() {
    var now = Date.now();
    if (pending && now - pendingAt < 300) return pending;
    pendingAt = now;
    pending = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        return {
          totalCents: cart.total_price,
          // Unlock products, or any subscription line: subscriptions ship free
          // at checkout via the "Free Shipping – Any Subscription in Cart"
          // automatic discount (a Shopify Function).
          unlocked: cart.items.some(function (item) {
            return UNLOCK_HANDLES.indexOf(item.handle) !== -1 ||
              !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
          })
        };
      })
      .catch(function () { return null; });
    return pending;
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Renders the bar for BOTH states from one fresh cart reading.
  function renderBar(el, cartCents, isUnlocked) {
    var goals;
    try {
      goals = JSON.parse(el.dataset.goals || '[]');
    } catch (e) {
      return;
    }
    if (!goals.length) return;

    var highest = goals[goals.length - 1].cents;
    if (!highest) return;

    var natural = (cartCents / highest) * 100;
    var width = natural;

    if (isUnlocked) {
      // Fill at least to the free-shipping badge (goal 1). With a single goal
      // that's 100%. If a gift tier is enabled later, the bar stops at the
      // free-shipping checkpoint and correctly leaves the gift portion empty.
      width = Math.max(natural, (goals[0].cents / highest) * 100);
    }
    width = Math.max(0, Math.min(100, width));

    var bar = el.querySelector('.cart-progress__bar__progress');
    if (bar) bar.style.width = width + '%';

    var text = el.querySelector('.cart-progress__text');
    if (!text) return;

    // First goal the cart hasn't reached on its own. When unlocked, skip goal 1
    // since it's already granted by the product.
    var next = null;
    for (var i = 0; i < goals.length; i++) {
      if (goals[i].cents > cartCents) {
        if (isUnlocked && i === 0) continue;
        next = goals[i];
        break;
      }
    }

    if (next) {
      var remaining = Math.max(0, next.cents - cartCents);
      text.innerHTML = (el.dataset.progressMessage || '')
        .replace('[amount]', '<span>' + money(remaining, el.dataset.shopCurrency) + '</span>')
        .replace('[next_goal]', next.text || '');
    } else {
      text.innerHTML = el.dataset.successMessage || '';
    }
  }

  function patch() {
    var Klass = customElements.get(ELEMENT);
    if (!Klass || Klass.prototype.__nciiPatched) return;

    var original = Klass.prototype.update;

    Klass.prototype.update = function () {
      var el = this;

      // Quantity mode isn't money-based; leave Shrine's behaviour untouched.
      if (el.dataset.isQuantity === 'true') return original.apply(el, arguments);

      // Shrine paints first so there's no empty flash, then we correct from
      // the live cart. We never write to el.dataset — that was the v2 bug.
      original.apply(el, arguments);

      readCart().then(function (state) {
        if (!state) return; // fetch failed: leave Shrine's render in place
        renderBar(el, state.totalCents, state.unlocked);
      });
    };

    Klass.prototype.__nciiPatched = true;
  }

  function refresh() {
    document.querySelectorAll(ELEMENT).forEach(function (el) {
      if (typeof el.update === 'function') el.update();
    });
  }

  if (customElements.get(ELEMENT)) {
    patch();
  } else {
    customElements.whenDefined(ELEMENT).then(patch);
  }

  refresh();

  // Re-check on cart mutations. Shrine re-renders the drawer via fetch, and a
  // freshly mounted element runs update() itself; this covers the case where
  // the existing element is reused instead.
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = (args[0] && args[0].url) || args[0] || '';
    return origFetch.apply(this, args).then(function (res) {
      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
        // Drop any cached read taken BEFORE this mutation. Without this, the
        // 300ms cache window and the refresh delay below can overlap and the
        // bar renders one cart operation behind.
        pending = null;
        pendingAt = 0;
        setTimeout(refresh, 200);
      }
      return res;
    });
  };

  document.addEventListener('cart:refresh', refresh);
})();
